import { useCallback, useEffect, useRef, useState } from "react";

const AUTO_HIDE_DELAY_MS = 3000;

/**
 * Hook that manages auto-hide behavior for floating overlays (toolbar, carousel, find bar).
 *
 * - Shows overlays on mouse move, keystroke, or touch.
 * - Hides them after ~3s of inactivity.
 * - Hovering over an overlay element keeps them visible (hover-locked).
 * - When `disableAutoHide` is true (e.g. find bar open), overlays stay visible.
 */
export const useOverlayAutoHide = ({
  isOpen,
  disableAutoHide,
}: {
  isOpen: boolean;
  disableAutoHide: boolean;
}) => {
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoverLockedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startHideTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      if (!isHoverLockedRef.current) {
        setIsOverlayVisible(false);
      }
    }, AUTO_HIDE_DELAY_MS);
  }, [clearTimer]);

  const showOverlay = useCallback(() => {
    setIsOverlayVisible(true);
    startHideTimer();
  }, [startHideTimer]);

  // Reset visibility when the modal opens
  useEffect(() => {
    if (isOpen) {
      setIsOverlayVisible(true);
      startHideTimer();
    }
    return clearTimer;
  }, [isOpen, startHideTimer, clearTimer]);

  // When auto-hide is disabled (e.g. find bar open), force overlays visible
  useEffect(() => {
    if (disableAutoHide) {
      clearTimer();
      setIsOverlayVisible(true);
    } else if (isOpen) {
      startHideTimer();
    }
  }, [disableAutoHide, isOpen, startHideTimer, clearTimer]);

  // Listen for mouse move, keystroke, and touch to show overlays
  useEffect(() => {
    if (!isOpen) return;

    const handleActivity = () => {
      showOverlay();
    };

    document.addEventListener("mousemove", handleActivity);
    document.addEventListener("keydown", handleActivity);
    document.addEventListener("touchstart", handleActivity);

    return () => {
      document.removeEventListener("mousemove", handleActivity);
      document.removeEventListener("keydown", handleActivity);
      document.removeEventListener("touchstart", handleActivity);
    };
  }, [isOpen, showOverlay]);

  /** Call from onMouseEnter on overlay elements to lock visibility while hovering. */
  const handleOverlayMouseEnter = useCallback(() => {
    isHoverLockedRef.current = true;
    clearTimer();
    setIsOverlayVisible(true);
  }, [clearTimer]);

  /** Call from onMouseLeave on overlay elements to unlock and restart the timer. */
  const handleOverlayMouseLeave = useCallback(() => {
    isHoverLockedRef.current = false;
    if (!disableAutoHide) {
      startHideTimer();
    }
  }, [disableAutoHide, startHideTimer]);

  return {
    isOverlayVisible: disableAutoHide || isOverlayVisible,
    handleOverlayMouseEnter,
    handleOverlayMouseLeave,
  };
};
